'use client';

import { useEffect, useState } from 'react';

import Link from 'next/link';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';

import { AuthLoadError } from '@/components/AuthLoadError';
import { OAuthButtons } from '@/components/OAuthButtons';
import { SiteLogoWide } from '@/components/SiteLogoWide';
import { useUserInfo } from '@/context/UserInfoContext';
import { beginAction, errorReason } from '@/feedback/actionBreadcrumb';
import { FEEDBACK_ACTION } from '@/feedback/ontology/types';
import { apiErrorMessage, type AuthCheckResponse, parseApiErrorResponse } from '@/lib/api';
import { API_URL } from '@/lib/consts';
import { OAuthIntent } from '@/lib/oauth';

enum LoginState {
  // On page load, no session established
  NotLoggedIn,

  // Sent credentials to backend, waiting for response
  // validating those credentials
  SendingCredentials,

  // Credentials validated, waiting for session to be created
  // on the backend and for auth requests to succeed
  WaitingForSession,

  // Backend is returning that the user cookie is valid
  SessionEstablished,

  // We've requested a redirect
  Redirecting,
}

/** Starting interval for the session-check polling loop after login submission. */
const INITIAL_POLL_INTERVAL_MS = 200;
/** Upper bound for the exponential backoff polling interval. */
const MAX_POLL_INTERVAL_MS = 5000;
/** Maximum time to wait for the backend session to become readable after login. */
const SESSION_CONFIRMATION_TIMEOUT_MS = 30_000;

/** Computes the next polling interval by multiplying the current interval by 1.6, capped at `MAX_POLL_INTERVAL_MS`. */
const backoffPollInterval = (currentIntervalMs: number): number => {
  return Math.min(Math.round(currentIntervalMs * 1.6), MAX_POLL_INTERVAL_MS);
};

interface LoginFormProps {
  destination: string;
  onLoginStart: () => void;
}

function resolveLoginDestination(rawRedirectPath: string | null): string {
  if (!rawRedirectPath) {
    return '/';
  }

  try {
    // In a 'use client' component, .origin is always present in the browser.
    // .href survives happy-dom's Location spreads when test mocks replace
    // location via Object.defineProperty and lose getters like .origin.
    const origin = window.location.origin || window.location.href;
    const parsed = new URL(rawRedirectPath, origin);

    // Reject cross-origin redirects (catches //, /\, and control-char bypasses
    // like /%0A/evil.com that the WHATWG parser normalizes to external origins).
    if (parsed.origin !== new URL(origin).origin) {
      return '/';
    }

    // Reject login loops
    if (parsed.pathname === '/login' || parsed.pathname.startsWith('/login?')) {
      return '/';
    }

    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return '/';
  }
}

const LoginForm: React.FC<LoginFormProps> = ({ destination, onLoginStart }) => {
  const auth_check_url = `${API_URL}/auth/check`;
  const [loginState, setLoginState] = useState<LoginState>(LoginState.NotLoggedIn);
  const [loginErrorMsg, setLoginErrorMsg] = useState<string>('');
  const [pollIntervalMs, setPollIntervalMs] = useState<number>(INITIAL_POLL_INTERVAL_MS);

  const { userInfo, fetchUserInfo } = useUserInfo();

  // Announce login state changes to screen readers
  // TODO(2): Not clear if this really should be done, we don't announce the login
  // state to non-screen-reader users...
  useEffect(() => {
    const message =
      loginState === LoginState.NotLoggedIn
        ? 'Login form ready'
        : loginState === LoginState.SendingCredentials
          ? 'Sending login credentials'
          : loginState === LoginState.WaitingForSession
            ? 'Waiting for session'
            : loginState === LoginState.SessionEstablished
              ? 'Session established'
              : loginState === LoginState.Redirecting
                ? 'Redirecting to home page'
                : 'Unknown login state';

    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    Object.assign(announcer.style, visuallyHidden);
    announcer.textContent = message;
    document.body.appendChild(announcer);

    return () => {
      document.body.removeChild(announcer);
    };
  }, [loginState]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    let cancelled = false;

    if (loginState === LoginState.WaitingForSession) {
      intervalId = setInterval(async () => {
        try {
          const res = await fetch(auth_check_url, { credentials: 'include' });
          if (!res.ok) {
            if (!cancelled) {
              setPollIntervalMs((prev) => backoffPollInterval(prev));
            }
            return;
          }

          const payload = (await res.json()) as AuthCheckResponse;
          if (payload.valid === true) {
            const nextUserInfo = await fetchUserInfo();
            if (!cancelled && nextUserInfo) {
              setLoginState(LoginState.SessionEstablished);
            } else if (!cancelled) {
              setPollIntervalMs((prev) => backoffPollInterval(prev));
            }
          } else {
            if (!cancelled) {
              setPollIntervalMs((prev) => backoffPollInterval(prev));
            }
          }
        } catch {
          if (!cancelled) {
            setPollIntervalMs((prev) => backoffPollInterval(prev));
          }
        }
      }, pollIntervalMs);
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [auth_check_url, fetchUserInfo, loginState, pollIntervalMs]);

  useEffect(() => {
    if (loginState !== LoginState.WaitingForSession) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setLoginErrorMsg('We couldn’t confirm your session. Please try signing in again.');
      setLoginState(LoginState.NotLoggedIn);
    }, SESSION_CONFIRMATION_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [loginState]);

  useEffect(() => {
    if (userInfo) {
      setLoginState((current) =>
        current === LoginState.Redirecting ? current : LoginState.Redirecting
      );
      if (typeof window !== 'undefined' && window.location.pathname !== destination) {
        window.location.replace(destination);
      }
    }
  }, [userInfo, destination]);

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onLoginStart();
    setLoginState(LoginState.SendingCredentials);
    setLoginErrorMsg('');
    setPollIntervalMs(INITIAL_POLL_INTERVAL_MS);

    const formData = new FormData(event.currentTarget);
    // Feedback breadcrumb: record the login attempt now; outcome is marked success/failure below.
    const action = beginAction(FEEDBACK_ACTION.LOG_IN);

    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username: formData.get('username'),
          password: formData.get('password'),
        }),
      });

      if (res.ok) {
        action.succeeded();
        setLoginState(LoginState.WaitingForSession);
      } else {
        action.failed({ error: `Server returned HTTP ${res.status}` });
        console.log('auth request fail', res);
        const errorEnvelope = await parseApiErrorResponse(res);
        setLoginErrorMsg(apiErrorMessage({ operation: 'login' }, res.status, errorEnvelope));
        setLoginState(LoginState.NotLoggedIn);
      }
    } catch (error) {
      action.failed({ error: errorReason(error) });
      console.log('auth request error', error);
      setLoginErrorMsg('An error occurred. Please try again.');
      setLoginState(LoginState.NotLoggedIn);
    }
  };

  return (
    // method="POST" shouldn't be required for a javascript handler, but we've
    // seen credentials occasionally appear in the URL bar (usually when there's
    // some backend problem) so we added this to see if it helps.
    <Box component="form" method="POST" onSubmit={handleLoginSubmit} aria-label="Login form">
      <Stack spacing={3}>
        <TextField
          id="username"
          name="username"
          label="Username or email"
          required
          fullWidth
          variant="outlined"
          aria-required="true"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          inputProps={{
            autoCapitalize: 'none',
            autoCorrect: 'off',
            spellCheck: false,
          }}
        />
        <TextField
          id="password"
          name="password"
          label="Password"
          type="password"
          required
          fullWidth
          variant="outlined"
          aria-required="true"
        />
        <Typography variant="body2" sx={{ textAlign: 'right' }}>
          <Link href="/reset-password/request">Forgot password?</Link>
        </Typography>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loginState !== LoginState.NotLoggedIn}
          aria-disabled={loginState !== LoginState.NotLoggedIn}
        >
          Login
        </Button>
        {loginState !== LoginState.NotLoggedIn && (
          <Alert severity="info" aria-live="polite" aria-atomic="true">
            {loginState === LoginState.SendingCredentials
              ? 'Logging in...'
              : loginState === LoginState.WaitingForSession
                ? 'Redirecting...'
                : loginState === LoginState.SessionEstablished
                  ? 'You are already logged in!'
                  : loginState === LoginState.Redirecting
                    ? 'Redirecting...'
                    : 'unknown login state'}
          </Alert>
        )}
        {loginErrorMsg && (
          <Alert severity="error" role="alert">
            {loginErrorMsg}
          </Alert>
        )}
        {/* <Typography variant="body2" sx={{ textAlign: 'center' }}>
          Don&apos;t have an account?{' '}
          <Link href="/register" style={{ color: 'inherit' }}>
            Register
          </Link>
        </Typography> */}
      </Stack>
    </Box>
  );
};

const Home: React.FC = () => {
  const { userInfoState, userInfo, reloadUserInfo } = useUserInfo();
  // Keeps login controls hidden after an auth probe error until the user explicitly continues.
  const [allowLoginAfterAuthError, setAllowLoginAfterAuthError] = useState(false);
  // Keeps a submitted login flow mounted while the authentication probe is loading or errored.
  const [hasLoginStarted, setHasLoginStarted] = useState(false);
  // Reads the raw post-login redirect query only where the browser location is available.
  const redirectPath =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('redirect')
      : null;
  // Restricts post-login navigation to a same-origin destination that cannot loop to login.
  const destination = resolveLoginDestination(redirectPath);
  // Re-hides login controls before retrying the failed authentication probe.
  const retryUserInfo = () => {
    setAllowLoginAfterAuthError(false);
    void reloadUserInfo();
  };

  useEffect(() => {
    if (userInfo && typeof window !== 'undefined') {
      window.location.replace(destination);
    }
  }, [userInfo, destination]);

  if (userInfo) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <SiteLogoWide heightPx={48} href={null} />
            </Box>
            <Alert severity="info" aria-live="polite" aria-atomic="true">
              {destination === '/' ? 'Redirecting to home page' : 'Redirecting…'}
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <SiteLogoWide heightPx={48} href={null} />
          </Box>
          {userInfoState.status === 'error' && !hasLoginStarted && (
            <AuthLoadError
              onRetry={retryUserInfo}
              onContinue={
                allowLoginAfterAuthError ? undefined : () => setAllowLoginAfterAuthError(true)
              }
            />
          )}
          {/* Avoid the initial flash without unmounting a user-started login flow. */}
          {(userInfoState.status === 'signed_out' ||
            hasLoginStarted ||
            allowLoginAfterAuthError) && (
            <>
              <LoginForm destination={destination} onLoginStart={() => setHasLoginStarted(true)} />
              <OAuthButtons intent={OAuthIntent.Login} />
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default Home;
